import { Component, Input, OnInit } from '@angular/core';
import { ISectorAir, ITicketSector } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { faArrowDown, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { EDirection } from "libraries/enums/e-direction";

export interface IAirportInformationDetail {
    fromAirportName: string;
    fromAirportCode?: string;
    fromCityName?: string;
    fromCityCode?: string;
    fromCountryName?: string;
    toCityCode?: string;
    toCountryName?: string;
    fromCountryCode?: string;
    toAirportName: string;
    toAirportCode?: string;
    toCityName?: string;
    toCountryCode?: string;
}

class Wording {
    public readonly terminal: string = Yii.t('wui-shared-js', 'Terminal');
    public readonly unTranslated: string = Yii.t('wui-shared-js', 'non traduit');
    public readonly unknown: string = Yii.t('wui-shared-js', 'inconnu');
}

@Component({
    selector: 'airport-details',
    template: `
        <div class="directions">
            <div class="airports-container airports-container-information">
                <fa-icon [icon]="iconCircle" aria-hidden="true"></fa-icon>
                <div class="airport-informations">
                    <strong class="airport-name" [class.undefined-data]="!sectorData.from.airport?.name">
                        <ng-container *ngIf="showAirportName && sectorData.from.airport; else codeOnly">
                            {{ airportInformationDetail.fromAirportName }}
                            <ng-container *ngIf="airportInformationDetail.fromAirportCode">
                                ({{ airportInformationDetail.fromAirportCode }})
                            </ng-container>
                        </ng-container>

                        <ng-template #codeOnly>
                            {{ airportInformationDetail.fromAirportCode }}
                        </ng-template>
                    </strong>
                    <em *ngIf="sectorData.from.city && sectorData.from.country && airportInformationDetail.fromCityName">
                        {{airportInformationDetail.fromCityName}}
                        ({{airportInformationDetail.fromCityCode}})
                    </em>
                    <em *ngIf="sectorData.from.city && sectorData.from.country && airportInformationDetail.fromCountryName">
                        {{airportInformationDetail.fromCountryName}}
                        ({{airportInformationDetail.fromCountryCode}})
                    </em>
                </div>
                <div class="terminal-informations">
                    <span *ngIf="sectorData.from.terminal?.code">
                        {{ labels.terminal }}
                        <strong>{{sectorData.from.terminal.code}}</strong>
                    </span>
                </div>
            </div>

            <div class="airports-container arrow-container">
                <fa-icon [icon]="iconArrowDown"></fa-icon>
            </div>

            <div class="airports-container airports-container-information">
                <fa-icon [icon]="iconCircle" aria-hidden="true"></fa-icon>
                <div class="airport-informations">
                    <strong class="airport-name" [class.undefined-data]="!sectorData.to?.airport?.name">
                        <ng-container *ngIf="showAirportName && sectorData.to.airport; else codeOnly">
                            {{ airportInformationDetail.toAirportName }}
                            <ng-container *ngIf="airportInformationDetail.toAirportCode">
                                ({{ airportInformationDetail.toAirportCode }})
                            </ng-container>
                        </ng-container>

                        <ng-template #codeOnly>
                            {{ airportInformationDetail.toAirportCode }}
                        </ng-template>
                    </strong>
                    <em *ngIf="sectorData.to.city && sectorData.to.country && airportInformationDetail.toCityName">
                        {{airportInformationDetail.toCityName}}
                        ({{airportInformationDetail.toCityCode}})
                    </em>
                    <em *ngIf="sectorData.to.city && sectorData.to.country && airportInformationDetail.toCountryName">
                        {{airportInformationDetail.toCountryName}}
                        ({{airportInformationDetail.toCountryCode}})
                    </em>
                </div>
                <div class="terminal-informations">
                    <span *ngIf="sectorData.to.terminal && sectorData.to.terminal.code">
                        {{ labels.terminal }} <strong>{{sectorData.to.terminal.code}}</strong>
                    </span>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./airport-details.component.scss']
})

export class AirportDetailsComponent implements OnInit {
    @Input()
    public sectorData: ITicketSector;
    @Input()
    public showAirportName: boolean = true;

    public iconCircle: IconDefinition = faCircle;

    public iconArrowDown: IconDefinition = faArrowDown;

    public labels: Wording = new Wording();

    public airportInformationDetail: IAirportInformationDetail;

    public ngOnInit(): void {
        this.airportInformationDetail = this.getAirportInformationDetail(this.sectorData);
    }

    private getAirportInformationDetail(sectorData: ITicketSector): IAirportInformationDetail {
        // Permet de prendre en compte le format de données des nouveaux ticket et celui des secteurs
        const airSector: ISectorAir = this.sectorData as unknown as ISectorAir;

        const hasFromSectorData: boolean = this.hasSectorData(airSector, EDirection.from);
        const hasToSectorData: boolean = this.hasSectorData(airSector, EDirection.to);
        const isUnknownFromData: boolean = typeof this.sectorData.from?.airport === 'string';
        const isUnknownToData: boolean = typeof this.sectorData.to?.airport === 'string';

        const fromDetails: Partial<IAirportInformationDetail> = isUnknownFromData ?
            {
                fromAirportName: sectorData.from.airport as unknown as string,
            } :
            hasFromSectorData ?
                {
                    fromAirportName: airSector.from.airport?.name || `${airSector.from.airport?.code ?? ''} ${this.labels.unTranslated}`,
                    fromAirportCode: airSector.from.airport?.code || this.labels.unknown,
                    fromCityName: airSector.from.city?.name,
                    fromCityCode: airSector.from.city?.code,
                    fromCountryName: airSector.from.country?.name,
                    fromCountryCode: airSector.from.country?.code,
                } :
                {
                    fromAirportName: sectorData.from.airport?.name?.value || `${sectorData.from.airport?.iata ?? ''} ${this.labels.unTranslated}`,
                    fromAirportCode: sectorData.from.airport?.iata || this.labels.unknown,
                    fromCityName: sectorData.from.city?.name?.value,
                    fromCityCode: sectorData.from.city?.code,
                    fromCountryName: sectorData.from.country?.name?.value,
                    fromCountryCode: sectorData.from.country?.iso,
                };

        const toDetails: Partial<IAirportInformationDetail> = isUnknownToData ?
            {
                toAirportName: sectorData.to.airport as unknown as string,
            } :
            hasToSectorData ?
                {
                    toAirportName: airSector.to.airport?.name || `${airSector.to.airport?.code ?? ''} ${this.labels.unTranslated}`,
                    toAirportCode: airSector.to.airport?.code || this.labels.unknown,
                    toCityName: airSector.to.city?.name,
                    toCityCode: airSector.to.city?.code,
                    toCountryName: airSector.to.country?.name,
                    toCountryCode: airSector.to.country?.code,
                } :
                {
                    toAirportName: sectorData.to.airport?.name?.value || `${sectorData.to.airport?.iata ?? ''} ${this.labels.unTranslated}`,
                    toAirportCode: sectorData.to.airport?.iata || this.labels.unknown,
                    toCityName: sectorData.to.city?.name?.value,
                    toCityCode: sectorData.to.city?.code,
                    toCountryName: sectorData.to.country?.name?.value,
                    toCountryCode: sectorData.to.country?.iso,
                };

        return {
            ...fromDetails,
            ...toDetails,
        } as unknown as IAirportInformationDetail;
    }

    private hasSectorData(sectorData: ISectorAir, fromOrTo: EDirection): boolean {

        const hasAirport: boolean = Boolean(sectorData[fromOrTo]?.airport);
        const hasAirportName: boolean = Boolean(sectorData[fromOrTo].airport?.name);
        const isAirportNameString: boolean = hasAirportName && typeof sectorData[fromOrTo].airport?.name === 'string';

        // @ts-ignore
        const hasAirportIata: boolean = sectorData[fromOrTo].airport?.iata;

        const hasCity: boolean = Boolean(sectorData[fromOrTo].city);
        const hasCityName: boolean = hasCity && Boolean(sectorData[fromOrTo].city.name);
        const isCityNameString: boolean = hasCityName && typeof sectorData[fromOrTo].city.name === 'string';

        const hasCountry: boolean = Boolean(sectorData[fromOrTo].city);
        const hasCountryName: boolean = hasCity && Boolean(sectorData[fromOrTo].city.name);
        const isCountryNameString: boolean = hasCityName && typeof sectorData[fromOrTo].city.name === 'string';

        return (hasAirport && !hasAirportName || isAirportNameString) &&
            !hasAirportIata &&
            (!hasCity || !hasCityName || isCityNameString) &&
            (!hasCountry || !hasCountryName || isCountryNameString);
    }
}

